import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const MainsPracticeManager = () => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [date, setDate] = useState("");
  const [questions, setQuestions] = useState([]);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [openSubmissions, setOpenSubmissions] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");

  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/mains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestions(res.data || []);
    } catch (err) {
      console.error("Failed to fetch mains questions:", err);
      alert("Failed to load mains questions. Please try again later.");
    }
  };

  const fetchSubmissions = async (questionId) => {
    if (openSubmissions === questionId) {
      setOpenSubmissions(null);
      return;
    }
    setLoadingSubmissions((prev) => ({ ...prev, [questionId]: true }));
    try {
      const res = await axios.get(
        `${API_BASE}/api/admin/mains/submissions/${questionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const subs = (res.data.submissions || []).map((sub) => ({
        ...sub,
        marksAwardedInput: sub.marksAwarded ?? "",
        evaluatorCommentInput: sub.evaluatorComment ?? "",
        editingEval: sub.evaluated ? false : true,
      }));

      setSubmissions((prev) => ({ ...prev, [questionId]: subs }));
      setOpenSubmissions(questionId);
    } catch (err) {
      console.error("Failed to fetch submissions:", err);
      alert("Failed to load submissions.");
    } finally {
      setLoadingSubmissions((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleAddQuestion = (e) => {
    e.preventDefault();
    if (!question.trim() || !date)
      return alert("Please enter both question and date.");
    setPendingQuestions((prev) => [
      ...prev,
      { question: question.trim(), modelAnswer: answer.trim(), date },
    ]);
    setQuestion("");
    setAnswer("");
    setDate("");
    setEditId(null);
  };

  const handleUploadAll = async () => {
    if (!pendingQuestions.length) return alert("No questions to upload.");
    if (
      !window.confirm(
        `Are you sure you want to upload ${pendingQuestions.length} questions?`
      )
    )
      return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/admin/mains/bulk`,
        { entries: pendingQuestions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Questions uploaded successfully!");
      setPendingQuestions([]);
      fetchQuestions();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    try {
      await axios.delete(`${API_BASE}/api/admin/mains/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchQuestions();
    } catch (err) {
      alert("Delete failed");
      console.error(err);
    }
  };

  const handleEdit = (q) => {
    setQuestion(q.question);
    setAnswer(q.modelAnswer || "");
    setDate(format(new Date(q.date), "yyyy-MM-dd"));
    setEditId(q._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRemovePending = (index) => {
    setPendingQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEvalChange = (questionId, subId, field, value) => {
    setSubmissions((prev) => ({
      ...prev,
      [questionId]: prev[questionId].map((sub) =>
        sub._id === subId ? { ...sub, [`${field}Input`]: value } : sub
      ),
    }));
  };

  const toggleEditEval = (questionId, subId) => {
    setSubmissions((prev) => ({
      ...prev,
      [questionId]: prev[questionId].map((sub) =>
        sub._id === subId ? { ...sub, editingEval: !sub.editingEval } : sub
      ),
    }));
  };

  const submitEvaluation = async (questionId, subId) => {
    const sub = submissions[questionId].find((s) => s._id === subId);
    if (!sub) return;

    const marks = sub.marksAwardedInput;
    const comment = sub.evaluatorCommentInput;

    if (marks === "") return alert("Enter marks before submitting.");

    try {
      await axios.put(
        `${API_BASE}/api/admin/mains/submissions/${subId}/evaluate`,
        { marksAwarded: marks, evaluatorComment: comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Evaluation submitted!");
      fetchSubmissions(questionId);
    } catch (err) {
      console.error(err);
      alert("Evaluation failed.");
    }
  };

  const groupByDate = () => {
    const safeQuestions = Array.isArray(questions)
      ? questions
      : Array.isArray(questions?.questions)
      ? questions.questions
      : [];

    const filtered = safeQuestions.filter((q) => {
      const matchesSearch =
        !searchTerm ||
        q.question.toLowerCase().includes(searchTerm.toLowerCase());
      const d = q.date ? new Date(q.date) : null;
      const year = d && !isNaN(d) ? d.getFullYear().toString() : "";
      const month =
        d && !isNaN(d) ? d.getMonth().toString() : "";

      const matchesYear = filterYear === "all" || year === filterYear;
      const matchesMonth = filterMonth === "all" || month === filterMonth;

      return matchesSearch && matchesYear && matchesMonth;
    });

    const grouped = {};

    filtered.forEach((q) => {
      if (!q?.date) return;
      const dateObj = new Date(q.date);
      if (isNaN(dateObj)) return;

      const year = dateObj.getFullYear();
      const monthNum = dateObj.getMonth();
      const day = String(dateObj.getDate()).padStart(2, "0");

      if (!grouped[year]) grouped[year] = {};
      if (!grouped[year][monthNum]) grouped[year][monthNum] = {};
      if (!grouped[year][monthNum][day]) grouped[year][monthNum][day] = [];

      grouped[year][monthNum][day].push(q);
    });

    return grouped;
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const groupedQuestions = groupByDate();

  const years = Array.from(
    new Set(
      (Array.isArray(questions) ? questions : questions?.questions || [])
        .filter((q) => q.date)
        .map((q) => new Date(q.date).getFullYear().toString())
    )
  ).sort((a, b) => Number(b) - Number(a));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              Mains Practice Admin
            </h1>
            <p className="text-xs text-slate-400">
              Manage daily questions, model answers and evaluations.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="rounded-full bg-emerald-500/10 px-3 py-1 text-emerald-400">
              {questions?.length || 0} questions
            </div>
            <div className="rounded-full bg-indigo-500/10 px-3 py-1 text-indigo-400">
              {pendingQuestions.length} pending
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 space-y-6">
        {/* Grid: form + pending list */}
        <div className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)]">
          {/* Form card */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 shadow-xl shadow-slate-950/40">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">
                  {editId ? "Edit Question" : "New Question"}
                </h2>
                <p className="text-xs text-slate-400">
                  Add or update daily mains question with model answer.
                </p>
              </div>
              {editId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditId(null);
                    setQuestion("");
                    setAnswer("");
                    setDate("");
                  }}
                  className="text-xs text-slate-400 hover:text-slate-200"
                >
                  Clear edit
                </button>
              )}
            </div>

            <form onSubmit={handleAddQuestion} className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Question
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Enter the mains question prompt..."
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-300">
                  Model Answer (optional)
                </label>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Add a concise, high-scoring model answer..."
                />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Date
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex gap-2 pt-5 sm:pt-6">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-xs font-medium text-white shadow-sm shadow-indigo-500/30 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-950"
                  >
                    {editId ? "Update & Add to List" : "Add to Pending"}
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadAll}
                    disabled={loading || pendingQuestions.length === 0}
                    className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow-sm shadow-emerald-500/30 hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading
                      ? "Uploading..."
                      : `Upload All (${pendingQuestions.length})`}
                  </button>
                </div>
              </div>
            </form>
          </section>

          {/* Pending list card */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 shadow-xl shadow-slate-950/40">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Pending to Upload</h3>
              <span className="text-xs text-slate-400">
                {pendingQuestions.length} items
              </span>
            </div>
            {pendingQuestions.length === 0 ? (
              <p className="text-xs text-slate-500">
                No pending questions. Add questions from the form on the left.
              </p>
            ) : (
              <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1 custom-scrollbar">
                {pendingQuestions.map((p, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-slate-800 bg-slate-900/80 px-3 py-2 text-xs"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="mb-1 text-[11px] font-semibold text-indigo-300">
                          {p.date
                            ? format(new Date(p.date), "dd MMM yyyy")
                            : "No date"}
                        </div>
                        <div className="line-clamp-3 text-slate-100">
                          {p.question}
                        </div>
                        {p.modelAnswer && (
                          <div className="mt-1 line-clamp-2 text-[11px] text-emerald-300">
                            Model: {p.modelAnswer}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePending(idx)}
                        className="rounded-full bg-slate-800 px-2 py-1 text-[10px] text-slate-300 hover:bg-slate-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Filters + list */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 shadow-xl shadow-slate-950/40">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-sm font-semibold">Existing Questions</h3>
              <p className="text-xs text-slate-400">
                Browse by year, month, and quickly search by question text.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Year</span>
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                  className="rounded-lg border border-slate-700 bg-slate-900/80 px-2 py-1 text-xs text-slate-100"
                >
                  <option value="all">All</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-slate-400">Month</span>
                <select
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="rounded-lg border border-slate-700 bg-slate-900/80 px-2 py-1 text-xs text-slate-100"
                >
                  <option value="all">All</option>
                  {monthNames.map((m, i) => (
                    <option key={m} value={String(i)}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative w-full md:w-52">
                <input
                  type="text"
                  placeholder="Search question..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900/80 px-8 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                />
                <span className="pointer-events-none absolute left-2 top-1.5 text-[11px] text-slate-500">
                  🔍
                </span>
              </div>
            </div>
          </div>

          {Object.keys(groupedQuestions).length === 0 ? (
            <p className="text-xs text-slate-500">
              No questions found for the current filters.
            </p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedQuestions)
                .sort((a, b) => Number(b[0]) - Number(a[0]))
                .map(([year, months]) => (
                  <div key={year} className="space-y-2">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      <span className="h-px flex-1 bg-slate-700" />
                      <span>{year}</span>
                      <span className="h-px flex-1 bg-slate-700" />
                    </div>

                    <div className="space-y-3">
                      {Object.entries(months)
                        .sort(
                          (a, b) => Number(b[0]) - Number(a[0])
                        )
                        .map(([monthNum, days]) => (
                          <div key={monthNum} className="space-y-2">
                            <div className="flex items-center gap-2 text-[11px] font-medium text-indigo-300">
                              <span>{monthNames[parseInt(monthNum, 10)]}</span>
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              {Object.entries(days)
                                .sort(
                                  (a, b) =>
                                    Number(b[0]) - Number(a[0])
                                )
                                .map(([day, qs]) =>
                                  qs.map((q) => (
                                    <article
                                      key={q._id}
                                      className="flex flex-col rounded-xl border border-slate-800 bg-slate-950/60 p-3 text-xs shadow-sm shadow-slate-950/60 hover:border-indigo-600/70 hover:shadow-md hover:shadow-indigo-500/10"
                                    >
                                      <div className="mb-2 flex items-center justify-between gap-2">
                                        <div className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-1 text-[10px] text-slate-300">
                                          <span>
                                            {String(day).padStart(2, "0")}{" "}
                                            {
                                              monthNames[
                                                parseInt(monthNum, 10)
                                              ]
                                            }{" "}
                                            {year}
                                          </span>
                                        </div>
                                        <span className="text-[10px] text-slate-500">
                                          ID: {q._id.slice(-6)}
                                        </span>
                                      </div>

                                      <div className="mb-2 text-[11px] font-semibold text-slate-100">
                                        Q. {q.question}
                                      </div>

                                      {q.modelAnswer && (
                                        <details className="mb-2 rounded-lg border border-emerald-700/50 bg-emerald-950/40 p-2">
                                          <summary className="cursor-pointer text-[10px] font-medium text-emerald-300">
                                            View Model Answer
                                          </summary>
                                          <div className="mt-1 whitespace-pre-wrap text-[11px] text-emerald-100">
                                            {q.modelAnswer}
                                          </div>
                                        </details>
                                      )}

                                      <div className="mt-auto flex items-center justify-between pt-1">
                                        <div className="flex gap-2 text-[10px]">
                                          <button
                                            onClick={() => handleEdit(q)}
                                            className="rounded-lg bg-slate-800 px-2 py-1 text-slate-200 hover:bg-slate-700"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleDelete(q._id)
                                            }
                                            className="rounded-lg bg-rose-600/90 px-2 py-1 text-rose-50 hover:bg-rose-500"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                        <button
                                          onClick={() =>
                                            fetchSubmissions(q._id)
                                          }
                                          className="text-[10px] text-indigo-300 hover:text-indigo-200"
                                        >
                                          {openSubmissions === q._id
                                            ? "Hide Submissions"
                                            : "View Submissions"}
                                        </button>
                                      </div>

                                      {openSubmissions === q._id && (
                                        <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/80 p-2">
                                          <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-slate-200">
                                            <span>Submissions</span>
                                            {loadingSubmissions[q._id] && (
                                              <span className="text-[10px] text-slate-400">
                                                Loading...
                                              </span>
                                            )}
                                          </div>

                                          {loadingSubmissions[q._id] ? (
                                            <p className="text-[10px] text-slate-400">
                                              Fetching submissions...
                                            </p>
                                          ) : submissions[q._id]?.length ===
                                            0 ? (
                                            <p className="text-[10px] text-slate-400">
                                              No submissions yet.
                                            </p>
                                          ) : (
                                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
                                              {submissions[q._id]?.map(
                                                (sub) => (
                                                  <div
                                                    key={sub._id}
                                                    className="rounded-lg border border-slate-800 bg-slate-950/70 p-2 text-[11px]"
                                                  >
                                                    <div className="mb-1 flex justify-between gap-2">
                                                      <div>
                                                        <p className="font-medium text-slate-100">
                                                          {sub.userName}
                                                        </p>
                                                        <p className="text-[10px] text-slate-400">
                                                          {sub.email}
                                                        </p>
                                                      </div>
                                                      <div className="text-right text-[10px] text-slate-400">
                                                        <p>
                                                          {sub.submittedAt
                                                            ? format(
                                                                new Date(
                                                                  sub.submittedAt
                                                                ),
                                                                "dd MMM, HH:mm"
                                                              )
                                                            : "N/A"}
                                                        </p>
                                                        <p>
                                                          {sub.fileUrl ? (
                                                            <a
                                                              href={
                                                                sub.fileUrl
                                                              }
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              className="text-indigo-300 underline"
                                                            >
                                                              {(
                                                                sub.fileType ||
                                                                "file"
                                                              ).toUpperCase()}
                                                            </a>
                                                          ) : (
                                                            "No file"
                                                          )}
                                                        </p>
                                                      </div>
                                                    </div>

                                                    {sub.editingEval ? (
                                                      <div className="mt-2 space-y-2 rounded-md bg-slate-900/80 p-2">
                                                        <div className="flex gap-2">
                                                          <input
                                                            type="number"
                                                            placeholder="Marks"
                                                            value={
                                                              sub.marksAwardedInput
                                                            }
                                                            onChange={(e) =>
                                                              handleEvalChange(
                                                                q._id,
                                                                sub._id,
                                                                "marksAwarded",
                                                                e.target.value
                                                              )
                                                            }
                                                            className="w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-100"
                                                          />
                                                          <input
                                                            type="text"
                                                            placeholder="Comment"
                                                            value={
                                                              sub.evaluatorCommentInput
                                                            }
                                                            onChange={(e) =>
                                                              handleEvalChange(
                                                                q._id,
                                                                sub._id,
                                                                "evaluatorComment",
                                                                e.target.value
                                                              )
                                                            }
                                                            className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-100"
                                                          />
                                                        </div>
                                                        <div className="flex gap-2">
                                                          <button
                                                            onClick={() =>
                                                              submitEvaluation(
                                                                q._id,
                                                                sub._id
                                                              )
                                                            }
                                                            className="flex-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-500"
                                                          >
                                                            Submit
                                                          </button>
                                                          {sub.evaluated && (
                                                            <button
                                                              onClick={() =>
                                                                toggleEditEval(
                                                                  q._id,
                                                                  sub._id
                                                                )
                                                              }
                                                              className="flex-1 rounded-md bg-amber-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-500"
                                                            >
                                                              Cancel
                                                            </button>
                                                          )}
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <div className="mt-2 flex items-center justify-between rounded-md bg-slate-900/70 p-2 text-[11px]">
                                                        <div className="space-y-0.5">
                                                          <p className="text-slate-200">
                                                            <span className="text-slate-400">
                                                              Marks:
                                                            </span>{" "}
                                                            {sub.marksAwarded}
                                                          </p>
                                                          <p className="text-slate-200">
                                                            <span className="text-slate-400">
                                                              Comment:
                                                            </span>{" "}
                                                            {
                                                              sub.evaluatorComment
                                                            }
                                                          </p>
                                                        </div>
                                                        <button
                                                          onClick={() =>
                                                            toggleEditEval(
                                                              q._id,
                                                              sub._id
                                                            )
                                                          }
                                                          className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-500"
                                                        >
                                                          Edit
                                                        </button>
                                                      </div>
                                                    )}
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </article>
                                  ))
                                )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default MainsPracticeManager;
